'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const basicAuth = require('../../lib/basicAuth');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:hub');

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * PhoenixHub is a web component responsible for loading more
     * items in a component footer.
     *
     * It reserves space based on breakpoints and number of items
     * for incoming elements before they load.
     *
     * @example
     *  <phoenix-hub>
     *      <div>Loading</div>
     *      <div>See More</div>
     *  </phoenix-hub>
     */

    class PhoenixHub extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return ['phoenix:breakpoint:changed'];
        }

        get observedEmittedEvents() {
            return ['phoenix:footer:seeMore:click'];
        }

        set breakpoint(val) {
            this._breakpoint = val;
        }

        get breakpoint() {
            if (!this._breakpoint) {
                this._breakpoint = pageClass.util.getCurrentBreakpoint();
            }
            return this._breakpoint;
        }

        get initialSlotsToken() {
            return this.footerContainer.getAttribute('initial-slots');
        }

        get siteBasePath() {
            return this.footerContainer.getAttribute('site-base-path');
        }

        connectedCallback() {
            super.connectedCallback();
            this.initialHeight = {};
            this.footerContainer = this.querySelector('.m-component-footer--container');
            this.footerLoader = this.querySelector('.m-footer-loader');
            this.initialHeight[this.breakpoint] = this.getBoundingClientRect().height;
            this.initialElements = this.children;
        }

        broadcastedEventCallback(name, args = {}) {
            switch (name) {
                case 'phoenix:breakpoint:changed':
                    this.breakpoint = args?.detail?.value;
                    this.onBreakpointChanged();
                    break;
            }
        }

        emittedEventCallback(name, args = {}) {
            switch (name) {
                case 'phoenix:footer:seeMore:click':
                    this.moreResultsToken = args?.detail?.moreResultsToken;
                    this.onSeeMoreClick();
                    break;
            }
        }

        onBreakpointChanged() {
            if (!this.initialHeight[this.breakpoint]) {
                // calculate height of child elements
                let totalHeight = 0;
                for (let el of this.initialElements) {
                    totalHeight += el.getBoundingClientRect().height;
                }
                this.initialHeight[this.breakpoint] = totalHeight;
            }
        }

        appendStream(targetElement, streamContainer) {
            const hubElement = targetElement.parentNode;
            hubElement.removeChild(targetElement);

            while (streamContainer.firstChild) {
                hubElement.appendChild(streamContainer.firstChild);
            }
        }

        buildStreamUrl() {
            const siteBasePath = this.siteBasePath || '';
            return [
                this.siteBasePath,
                '/.api/stream-html',
                window.location.pathname.replace(siteBasePath, ''),
                '?moreResultsToken=',
                this.moreResultsToken,
                '&initialSlots=',
                this.initialSlotsToken,
                // Need to preserve any existing query string, for search pages
                window.location.search !== '' ? '&' + window.location.search.substring(1) : '',
            ].join('');
        }

        fetchItems() {
            const url = this.buildStreamUrl();
            const opts = basicAuth.getRequestOptions();

            fetch(url, opts)
                .then((response) => {
                    return response.json();
                })
                .then((data) => {
                    if (typeof data.html === 'string' && data.html !== '') {
                        const streamContainer = document.createElement('div');
                        streamContainer.innerHTML = data.html;
                        this.style.setProperty('--phx-hub-loader-spacing', 0);
                        this.appendStream(this.footerContainer, streamContainer);
                        this.footerContainer = this.querySelector('.m-component-footer--container');
                        this.footerLoader = this.querySelector('.m-footer-loader');
                        window.dispatchEvent(new window.CustomEvent('contentAdded'));
                    }
                });

            // No need for a corresponding hide function because we're replacing with the new footer
            this.footerLoader.classList.add('is-loading');
        }

        onSeeMoreClick() {
            // reserve space immediately after the click
            let stickyParent = this.closest('.is-sticky-component');
            if (stickyParent) {
                // the stickiness will prevent space at the bottom from showing
                stickyParent.classList.remove('is-sticky-component');
            }
            this.style.setProperty('--phx-hub-loader-spacing', this.initialHeight[this.breakpoint]);
            // fetch more items
            this.fetchItems();
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._removeListener();
        }
    }

    window.customElements.define('phoenix-hub', PhoenixHub);
    measure.end();
});
